/**
 * CL-CART-01 — browser persistence for the cart (decision D-1).
 *
 * This is the guest-and-first-load layer. Once the buyer logs in, cart.duck.js also
 * mirrors the cart into their own privateData, which is what makes it follow them to
 * another device.
 *
 * Every function is safe during server-side rendering: there is no `window` on the
 * server, so reads return an empty cart and writes do nothing.
 */

import { emptyCart, sanitizeCart } from './cart';

export const CART_STORAGE_KEY = 'clastique_cart_v1';

const hasStorage = () => typeof window !== 'undefined' && !!window.localStorage;

/**
 * Read the stored cart, or an empty one.
 *
 * `currentUserId` is not optional in spirit: the D-1b ownership check happens HERE so
 * there is no way to read a cart without it. Leaving it to callers is how a rule ends up
 * enforced in one place and missed in another.
 */
export const loadCart = (nowIso, currentUserId = null) => {
  if (!hasStorage()) return emptyCart();

  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return emptyCart();

    // One gate: schema version, retention (D-3), ownership (D-1b) and the per-seller cap
    // (D-4). A future v2 needs a migration inside sanitizeCart, or release day silently
    // empties every existing cart.
    return sanitizeCart(JSON.parse(raw), { nowIso, currentUserId });
  } catch (e) {
    // Reads throw too, not just writes: Safari in private mode and some enterprise
    // policies refuse getItem outright.
    return emptyCart();
  }
};

export const saveCart = cart => {
  if (!hasStorage()) return;
  try {
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (e) {
    // Private browsing and a full quota both throw. A cart that fails to persist is a
    // much smaller problem than a page that crashes on every add.
  }
};

/** Used on logout (D-1a), and whenever a cart is discarded. */
export const clearStoredCart = () => {
  if (!hasStorage()) return;
  try {
    window.localStorage.removeItem(CART_STORAGE_KEY);
  } catch (e) {
    // Nothing to do: the cart is already unreadable to us.
  }
};
